import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useExpenses, useCategories } from '../hooks/useApi'
import { formatCurrency, formatDate, getCurrentDateString } from '../lib/utils'
import { Card, CardContent } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog'
import { Checkbox } from '../components/ui/checkbox'
import { Switch } from '../components/ui/switch'
import { toast } from 'sonner'
import { Plus, Receipt, Edit2, Trash2, Loader2, TrendingDown, Lock, Zap, RefreshCw, Calendar, Camera, Upload, Mail, PenLine } from 'lucide-react'
import { cn } from '../lib/utils'
import { analytics } from '../lib/analytics'

// Helper functions for date filtering
const getStartOfWeek = () => {
  const now = new Date()
  const day = now.getDay()
  const diff = now.getDate() - day + (day === 0 ? -6 : 1)
  const monday = new Date(now.setDate(diff))
  return monday.toISOString().split('T')[0]
}

const getStartOfMonth = () => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
}

const getStartOfQuarter = () => {
  const now = new Date()
  const quarter = Math.floor(now.getMonth() / 3)
  return new Date(now.getFullYear(), quarter * 3, 1).toISOString().split('T')[0]
}

const getStartOfYear = () => {
  const now = new Date()
  return new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0]
}

export default function Expenses() {
  const [expenses, setExpenses] = useState([])
  const [categories, setCategories] = useState([])
  const [editingExpense, setEditingExpense] = useState(null)
  const [deleteId, setDeleteId] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  const [timePeriod, setTimePeriod] = useState('month')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [showCustomDates, setShowCustomDates] = useState(false)
  const [filterCategory, setFilterCategory] = useState('all')
  const [filterType, setFilterType] = useState('all') // 'all', 'fixed', 'variable'
  const navigate = useNavigate()
  
  const { fetchExpenses, createExpense, updateExpense, deleteExpense, loading } = useExpenses()
  const { fetchCategories } = useCategories()

  const [form, setForm] = useState({
    date: getCurrentDateString(),
    amount: '',
    merchant: '',
    primary_type: 'variable',
    category_id: '',
    non_deductible: false,
    notes: '',
    // Recurrence fields
    is_recurring: false,
    frequency: 'monthly',
    recurrence_start_date: getCurrentDateString(),
    recurrence_end_date: ''
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [expData, catData] = await Promise.all([
        fetchExpenses(),
        fetchCategories()
      ])
      setExpenses(expData)
      setCategories(catData)
    } catch (err) {
      toast.error('Failed to load data')
    }
  }

  // Group categories by type and group
  const groupedCategories = useMemo(() => {
    const fixed = {}
    const variable = {}
    
    categories.forEach(cat => {
      const type = cat.primary_type || 'variable'
      const group = cat.group || 'Other'
      
      if (type === 'fixed') {
        if (!fixed[group]) fixed[group] = []
        fixed[group].push(cat)
      } else {
        if (!variable[group]) variable[group] = []
        variable[group].push(cat)
      }
    })
    
    return { fixed, variable }
  }, [categories])

  // Get categories for current primary type
  const currentCategories = useMemo(() => {
    return form.primary_type === 'fixed' ? groupedCategories.fixed : groupedCategories.variable
  }, [form.primary_type, groupedCategories])

  const handleSubmit = async () => {
    if (!form.amount || !form.merchant || !form.category_id) {
      toast.error('Please fill in required fields')
      return
    }

    try {
      const expenseData = {
        ...form,
        amount: parseFloat(form.amount)
      }

      if (editingExpense) {
        await updateExpense(editingExpense.id, expenseData)
        toast.success('Expense updated')
      } else {
        await createExpense(expenseData)
        analytics.receiptSaved(form.category_id, parseFloat(form.amount))
        toast.success('Expense added')
      }
      
      setDialogOpen(false)
      resetForm()
      loadData()
    } catch (err) {
      toast.error('Failed to save expense')
    }
  }

  const handleEdit = (expense) => {
    setEditingExpense(expense)
    setForm({
      date: expense.date,
      amount: expense.amount.toString(),
      merchant: expense.merchant,
      primary_type: expense.primary_type || 'variable',
      category_id: expense.category_id,
      non_deductible: expense.non_deductible || false,
      notes: expense.notes || '',
      is_recurring: expense.is_recurring || false,
      frequency: expense.frequency || 'monthly',
      recurrence_start_date: expense.recurrence_start_date || getCurrentDateString(),
      recurrence_end_date: expense.recurrence_end_date || ''
    })
    setDialogOpen(true)
  }

  const handleDelete = async () => {
    try {
      await deleteExpense(deleteId)
      toast.success('Expense deleted')
      setDeleteId(null)
      loadData()
    } catch (err) {
      toast.error('Failed to delete')
    }
  }

  const resetForm = () => {
    setEditingExpense(null)
    setForm({
      date: getCurrentDateString(),
      amount: '',
      merchant: '',
      primary_type: 'variable',
      category_id: '',
      non_deductible: false,
      notes: '',
      is_recurring: false,
      frequency: 'monthly',
      recurrence_start_date: getCurrentDateString(),
      recurrence_end_date: ''
    })
  }

  // Handle primary type change - reset category and recurrence
  const handlePrimaryTypeChange = (type) => {
    setForm(f => ({ 
      ...f, 
      primary_type: type, 
      category_id: '',
      // Reset recurrence when switching to variable
      is_recurring: type === 'variable' ? false : f.is_recurring
    }))
  }

  // Handle time period selection
  const handleTimePeriodChange = (period) => {
    setTimePeriod(period)
    setShowCustomDates(period === 'custom')
  }

  // Get date range based on time period
  const getDateRange = () => {
    if (timePeriod === 'custom' && customStartDate && customEndDate) {
      return { start: customStartDate, end: customEndDate }
    }
    
    const today = new Date()
    let start, end
    
    switch (timePeriod) {
      case 'week':
        start = getStartOfWeek()
        end = getCurrentDateString()
        break
      case 'month':
        start = getStartOfMonth()
        end = getCurrentDateString()
        break
      case 'quarter':
        start = getStartOfQuarter()
        end = getCurrentDateString()
        break
      case 'ytd':
        start = getStartOfYear()
        end = getCurrentDateString()
        break
      case 'all':
        start = '2000-01-01' // Far enough back to include everything
        end = '2099-12-31'
        break
      default:
        start = getStartOfMonth()
        end = getCurrentDateString()
    }
    
    return { start, end }
  }

  // Filter expenses by date range
  const { start: startDate, end: endDate } = getDateRange()
  const filteredExpenses = expenses.filter(e => {
    const dateMatch = e.date >= startDate && e.date <= endDate
    const categoryMatch = filterCategory === 'all' || e.category_id === filterCategory
    const typeMatch = filterType === 'all' || (e.primary_type || 'variable') === filterType
    return dateMatch && categoryMatch && typeMatch
  })

  // Calculate totals
  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount, 0)
  const fixedTotal = filteredExpenses.filter(e => e.primary_type === 'fixed').reduce((sum, e) => sum + e.amount, 0)
  const variableTotal = filteredExpenses.filter(e => (e.primary_type || 'variable') === 'variable').reduce((sum, e) => sum + e.amount, 0)

  // Category breakdown
  const categoryTotals = filteredExpenses.reduce((acc, e) => {
    acc[e.category_name] = (acc[e.category_name] || 0) + e.amount
    return acc
  }, {})
  const sortedCategories = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1])

  const periodLabels = {
    week: 'This Week',
    month: 'This Month',
    quarter: 'This Quarter',
    ytd: 'Year to Date',
    all: 'All Time',
    custom: customStartDate && customEndDate ? `${customStartDate} to ${customEndDate}` : 'Custom Range'
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="expenses-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight font-['Barlow_Condensed']">
            EXPENSES
          </h1>
          <p className="text-muted-foreground">
            Track and manage your expenses
          </p>
        </div>
        
        {/* Add Menu Dialog */}
        <Dialog open={addMenuOpen} onOpenChange={setAddMenuOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-expense-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Expense</DialogTitle>
              <DialogDescription>
                Choose how you want to add an expense
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 py-4">
              <Button
                variant="outline"
                className="h-14 justify-start gap-3"
                onClick={() => {
                  setAddMenuOpen(false)
                  navigate('/scanner?type=receipt')
                }}
                data-testid="expense-scan-option"
              >
                <Camera className="w-5 h-5 text-yellow-500" />
                <div className="text-left">
                  <p className="font-medium">Scan Receipt</p>
                  <p className="text-xs text-muted-foreground">Use camera to scan</p>
                </div>
              </Button>
              <Button
                variant="outline"
                className="h-14 justify-start gap-3"
                onClick={() => {
                  setAddMenuOpen(false)
                  navigate('/scanner?type=receipt&upload=true')
                }}
                data-testid="expense-upload-option"
              >
                <Upload className="w-5 h-5 text-yellow-500" />
                <div className="text-left">
                  <p className="font-medium">Upload Image</p>
                  <p className="text-xs text-muted-foreground">Upload from device</p>
                </div>
              </Button>
              <Button
                variant="outline"
                className="h-14 justify-start gap-3"
                onClick={() => {
                  setAddMenuOpen(false)
                  navigate('/scanner?type=receipt&email=true')
                }}
                data-testid="expense-email-option"
              >
                <Mail className="w-5 h-5 text-yellow-500" />
                <div className="text-left">
                  <p className="font-medium">From Email</p>
                  <p className="text-xs text-muted-foreground">Paste email content</p>
                </div>
              </Button>
              <Button
                variant="outline"
                className="h-14 justify-start gap-3"
                onClick={() => {
                  setAddMenuOpen(false)
                  setDialogOpen(true)
                }}
                data-testid="expense-manual-option"
              >
                <PenLine className="w-5 h-5 text-yellow-500" />
                <div className="text-left">
                  <p className="font-medium">Manual Entry</p>
                  <p className="text-xs text-muted-foreground">Enter details manually</p>
                </div>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Manual Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 sm:p-6">
          <div className="p-6 pb-0 sm:p-0">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] text-xl">
                {editingExpense ? 'EDIT EXPENSE' : 'ADD EXPENSE'}
              </DialogTitle>
            </DialogHeader>
          </div>
          
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-6 sm:px-0">
            <div className="space-y-4 py-4">
              {/* Primary Type Toggle */}
              <div className="space-y-2">
                <Label>Expense Type *</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={form.primary_type === 'fixed' ? 'default' : 'outline'}
                    className={cn(
                      "h-16 flex flex-col gap-1",
                      form.primary_type === 'fixed' && "bg-purple-600 hover:bg-purple-700"
                    )}
                    onClick={() => handlePrimaryTypeChange('fixed')}
                    data-testid="type-fixed-btn"
                  >
                    <Lock className="w-5 h-5" />
                    <span className="text-sm font-semibold">Fixed Costs</span>
                  </Button>
                  <Button
                    type="button"
                    variant={form.primary_type === 'variable' ? 'default' : 'outline'}
                    className={cn(
                      "h-16 flex flex-col gap-1",
                      form.primary_type === 'variable' && "bg-yellow-600 hover:bg-yellow-700"
                    )}
                    onClick={() => handlePrimaryTypeChange('variable')}
                    data-testid="type-variable-btn"
                  >
                    <Zap className="w-5 h-5" />
                    <span className="text-sm font-semibold">Variable Costs</span>
                  </Button>
                </div>
              </div>

              {/* Category Selection - Grouped */}
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={form.category_id}
                  onValueChange={(v) => setForm(f => ({ ...f, category_id: v }))}
                >
                  <SelectTrigger className="h-12" data-testid="expense-form-category">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {Object.entries(currentCategories).map(([group, cats]) => (
                      <div key={group}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-secondary/50 sticky top-0">
                          {group}
                        </div>
                        {cats.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id} className="pl-4">
                            {cat.name}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                    className="h-12"
                    data-testid="expense-form-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Amount *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="h-12 text-lg font-semibold"
                    data-testid="expense-form-amount"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Merchant/Vendor *</Label>
                <Input
                  placeholder="e.g., Pilot Flying J"
                  value={form.merchant}
                  onChange={(e) => setForm(f => ({ ...f, merchant: e.target.value }))}
                  className="h-12"
                  data-testid="expense-form-merchant"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  placeholder="Any additional notes..."
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="h-12"
                  data-testid="expense-form-notes"
                />
              </div>

              {/* Non-deductible Checkbox */}
              <div className="flex items-center space-x-3 p-3 bg-secondary/50 rounded-lg">
                <Checkbox
                  id="non-deductible"
                  checked={form.non_deductible}
                  onCheckedChange={(checked) => setForm(f => ({ ...f, non_deductible: checked }))}
                  data-testid="expense-form-non-deductible"
                />
                <div className="grid gap-0.5 leading-none">
                  <label
                    htmlFor="non-deductible"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Non-deductible / Special
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Exclude from tax estimates (still included in P&L and cost-per-mile)
                  </p>
                </div>
              </div>

              {/* Recurring Expense Section - Only for Fixed Costs */}
              {form.primary_type === 'fixed' && !editingExpense?.is_generated && (
                <div className="space-y-3 p-4 border border-purple-500/30 rounded-lg bg-purple-500/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-purple-500" />
                      <Label className="text-sm font-medium">Make this recurring?</Label>
                    </div>
                    <Switch
                      checked={form.is_recurring}
                      onCheckedChange={(checked) => setForm(f => ({ ...f, is_recurring: checked }))}
                      data-testid="expense-form-recurring-toggle"
                    />
                  </div>
                  
                  {form.is_recurring && (
                    <div className="space-y-3 pt-2 border-t border-purple-500/20">
                      <div className="space-y-2">
                        <Label className="text-xs">Frequency</Label>
                        <div className="grid grid-cols-3 gap-2">
                          {['weekly', 'monthly', 'yearly'].map((freq) => (
                            <Button
                              key={freq}
                              type="button"
                              variant={form.frequency === freq ? 'default' : 'outline'}
                              size="sm"
                              className={cn(
                                "h-9",
                                form.frequency === freq && "bg-purple-600 hover:bg-purple-700"
                              )}
                              onClick={() => setForm(f => ({ ...f, frequency: freq }))}
                              data-testid={`frequency-${freq}`}
                            >
                              {freq.charAt(0).toUpperCase() + freq.slice(1)}
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Start Date</Label>
                          <Input
                            type="date"
                            value={form.recurrence_start_date}
                            onChange={(e) => setForm(f => ({ ...f, recurrence_start_date: e.target.value }))}
                            className="h-10"
                            data-testid="recurrence-start-date"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">End Date (optional)</Label>
                          <Input
                            type="date"
                            value={form.recurrence_end_date}
                            onChange={(e) => setForm(f => ({ ...f, recurrence_end_date: e.target.value }))}
                            className="h-10"
                            placeholder="Indefinite"
                            data-testid="recurrence-end-date"
                          />
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Auto-generates entries {form.frequency} from start date
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Show indicator for generated expenses */}
              {editingExpense?.is_generated && (
                <div className="flex items-center gap-2 p-3 bg-purple-500/10 rounded-lg text-sm">
                  <RefreshCw className="w-4 h-4 text-purple-500" />
                  <span className="text-muted-foreground">
                    This is an auto-generated recurring expense. Edit or delete as needed.
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Save Button - Sticky footer */}
          <div className="flex-shrink-0 p-4 sm:p-0 sm:pt-4 bg-background border-t sm:border-t-0 border-border">
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full h-14 sm:h-12 rounded-full font-bold text-lg sm:text-base bg-yellow-500 hover:bg-yellow-600 text-black"
              data-testid="save-expense-form-btn"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin mr-2" />}
              {form.is_recurring && !editingExpense ? 'Save Recurring Expense' : (editingExpense ? 'Update Expense' : 'Save Expense')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Time Period Selector */}
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          {['week', 'month', 'quarter'].map((period) => (
            <Button
              key={period}
              variant={timePeriod === period ? 'default' : 'outline'}
              className="h-10"
              onClick={() => handleTimePeriodChange(period)}
              data-testid={`period-${period}`}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </Button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Button
            variant={timePeriod === 'ytd' ? 'default' : 'outline'}
            className="h-10"
            onClick={() => handleTimePeriodChange('ytd')}
            data-testid="period-ytd"
          >
            YTD
          </Button>
          <Button
            variant={timePeriod === 'all' ? 'default' : 'outline'}
            className="h-10"
            onClick={() => handleTimePeriodChange('all')}
            data-testid="period-all"
          >
            All Time
          </Button>
          <Button
            variant={timePeriod === 'custom' ? 'default' : 'outline'}
            className="h-10"
            onClick={() => handleTimePeriodChange('custom')}
            data-testid="period-custom"
          >
            Custom
          </Button>
        </div>
        
        {/* Custom Date Range Picker */}
        {showCustomDates && (
          <div className="grid grid-cols-2 gap-3 p-3 bg-secondary/50 rounded-lg">
            <div className="space-y-1">
              <Label className="text-xs">Start Date</Label>
              <Input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="h-10"
                data-testid="custom-start-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End Date</Label>
              <Input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="h-10"
                data-testid="custom-end-date"
              />
            </div>
          </div>
        )}
      </div>

      {/* Summary Card */}
      <Card className="bg-yellow-500/10 border-yellow-500/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{periodLabels[timePeriod]}</p>
              <p className="text-3xl font-bold text-yellow-500 kpi-value">
                {formatCurrency(totalExpenses, 0)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {filteredExpenses.length} expense{filteredExpenses.length !== 1 ? 's' : ''}
              </p>
            </div>
            <TrendingDown className="w-12 h-12 text-yellow-500/50" />
          </div>
        </CardContent>
      </Card>

      {/* Fixed vs Variable Breakdown */}
      <div className="grid grid-cols-2 gap-3">
        <Card 
          className={cn(
            "cursor-pointer transition-all",
            filterType === 'fixed' ? "ring-2 ring-purple-500" : "hover:bg-secondary/50"
          )}
          onClick={() => setFilterType(filterType === 'fixed' ? 'all' : 'fixed')}
        >
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Lock className="w-4 h-4 text-purple-500" />
              <span className="text-xs font-semibold uppercase text-muted-foreground">Fixed</span>
            </div>
            <p className="text-xl font-bold text-purple-500">{formatCurrency(fixedTotal, 0)}</p>
          </CardContent>
        </Card>
        <Card 
          className={cn(
            "cursor-pointer transition-all",
            filterType === 'variable' ? "ring-2 ring-yellow-500" : "hover:bg-secondary/50"
          )}
          onClick={() => setFilterType(filterType === 'variable' ? 'all' : 'variable')}
        >
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-yellow-500" />
              <span className="text-xs font-semibold uppercase text-muted-foreground">Variable</span>
            </div>
            <p className="text-xl font-bold text-yellow-500">{formatCurrency(variableTotal, 0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown */}
      {sortedCategories.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 font-['Barlow_Condensed'] uppercase tracking-wide text-sm">
              By Category
            </h3>
            <div className="space-y-2">
              {sortedCategories.slice(0, 5).map(([category, total]) => (
                <div key={category} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                  <span className="text-sm">{category}</span>
                  <span className="font-semibold text-yellow-500">{formatCurrency(total)}</span>
                </div>
              ))}
              {sortedCategories.length > 5 && (
                <p className="text-xs text-muted-foreground text-center pt-2">
                  +{sortedCategories.length - 5} more categories
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Filter:</span>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48 h-10" data-testid="expense-filter">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filterType !== 'all' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilterType('all')}
            className="text-xs"
          >
            Clear filter
          </Button>
        )}
      </div>

      {/* Expenses List */}
      <div className="space-y-3">
        {filteredExpenses.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Receipt className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No expenses for {periodLabels[timePeriod].toLowerCase()}</p>
              <p className="text-sm text-muted-foreground mb-4">
                Add expenses from the Dashboard
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredExpenses.map((expense) => (
            <Card key={expense.id} className="card-interactive" data-testid={`expense-item-${expense.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={cn(
                      "category-icon relative",
                      expense.primary_type === 'fixed' ? "bg-purple-500/10" : "bg-yellow-500/10"
                    )}>
                      {expense.primary_type === 'fixed' ? (
                        <Lock className="w-5 h-5 text-purple-500" />
                      ) : (
                        <Receipt className="w-5 h-5 text-yellow-500" />
                      )}
                      {(expense.is_recurring || expense.is_generated) && (
                        <RefreshCw className="w-3 h-3 text-purple-500 absolute -bottom-1 -right-1 bg-background rounded-full" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{expense.merchant}</p>
                        {expense.is_recurring && (
                          <span className="text-xs bg-purple-500/20 text-purple-500 px-1.5 py-0.5 rounded">
                            {expense.frequency}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {expense.category_name} • {formatDate(expense.date)}
                        {expense.non_deductible && (
                          <span className="ml-2 text-xs bg-secondary px-1.5 py-0.5 rounded">Non-ded</span>
                        )}
                        {expense.is_generated && (
                          <span className="ml-2 text-xs bg-purple-500/10 text-purple-400 px-1.5 py-0.5 rounded">Auto</span>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <p className={cn(
                      "font-semibold whitespace-nowrap",
                      expense.primary_type === 'fixed' ? "text-purple-500" : "text-yellow-500"
                    )}>
                      -{formatCurrency(expense.amount)}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(expense)}
                      data-testid={`edit-expense-${expense.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteId(expense.id)}
                      data-testid={`delete-expense-${expense.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Recurring Expenses Note */}
      {filteredExpenses.some(e => e.is_recurring || e.is_generated) && (
        <p className="text-xs text-muted-foreground text-center">
          <RefreshCw className="w-3 h-3 inline mr-1" />
          Recurring expenses are auto-generated for planning – edit or delete any entry as needed.
        </p>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this expense record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-expense">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-expense">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
